/**
 * AgentDrawerTabs Component
 *
 * Displays the tab navigation for the agent drawer.
 * Pure presentation component - all state management handled by parent.
 *
 * @see AssetDrawerTabs for reference implementation
 * @see TechnologyDrawerTabs for similar pattern
 */

import { TabOption } from '@praetorian-chariot/ui';

import TabsComponent from '@/components/Tabs';

export interface AgentDrawerTabsProps {
  /** Tab configuration array from useAgentTabs hook */
  tabs: TabOption[];
  /** Current active tab ID from useAgentDrawerTab hook */
  currentTab: string;
  /** Callback to change tab (from useAgentDrawerTab hook) */
  onTabChange: (tab: string) => void;
}

/**
 * AgentDrawerTabs renders the tab navigation system for agent details:
 * - Overview, Health, Credentials tabs (all users)
 * - Tasks, Tunnel, Users, Jobs tabs (Praetorian only)
 * - Keyboard navigation via local Tabs component
 * - Tab state managed by parent via useAgentDrawerTab hook (local state + URL sync)
 *
 * This is a pure presentation component with NO state management.
 *
 * @example
 * ```tsx
 * const tabs = useAgentTabs(isPraetorianUser, hasActiveTasks);
 * const [currentTab, setCurrentTab] = useAgentDrawerTab();
 * <AgentDrawerTabs tabs={tabs} currentTab={currentTab} onTabChange={setCurrentTab} />
 * ```
 */
export function AgentDrawerTabs({
  tabs,
  currentTab,
  onTabChange,
}: AgentDrawerTabsProps) {
  // Find selected tab object for Tabs component
  const selectedTab = tabs.find(tab => tab.value === currentTab) || tabs[0];

  // Handle tab selection - delegate to parent
  const handleTabSelect = (tabOption: TabOption) => {
    onTabChange(tabOption.value);
  };

  return (
    <div className="-mt-4">
      <TabsComponent
        id="agentDetailsTabs"
        keyboardDependencies={[]}
        tabs={tabs}
        selected={selectedTab}
        onSelect={handleTabSelect}
        data-testid="agent-drawer-tabs"
      />
    </div>
  );
}
